import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Flame, Beef, Wheat, Droplet, Plus, Repeat, RotateCcw, X, Loader2, Check,
  Dumbbell, Moon, Lock, ChefHat, Sparkles, Salad, Download, ShoppingCart, Printer, Mail, WifiOff,
  CheckSquare, Square, ListChecks,
} from 'lucide-react';
import { useClientAuth } from '../../context/ClientAuthContext';
import { SmartShoppingListModal } from './SmartShoppingListModal';

const DAYS = { fr: ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'], en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] };
const todayStr = () => new Date().toLocaleDateString('en-CA');

/* ---------- Modal de remplacement d'un repas (flexibilité) ---------- */
const SwapModal = ({ en, api, targetMacros, onClose, onPicked, busy }) => {
  const [tab, setTab] = useState('personal');
  const [personal, setPersonal] = useState(null);
  const [library, setLibrary] = useState(null);
  const [premium, setPremium] = useState(null);
  const [manual, setManual] = useState({ food_name: '', calories: '', protein: '', carbs: '', fat: '' });

  useEffect(() => {
    api('/api/personal-recipes').then((d) => setPersonal(d.recipes || [])).catch(() => setPersonal([]));
    api('/api/tracking/subscription').then((d) => {
      setPremium(!!d.premium);
      if (d.premium) api('/api/recipes').then((r) => setLibrary(r.recipes || [])).catch(() => setLibrary([]));
    }).catch(() => setPremium(false));
  }, [api]);

  const suggestions = React.useMemo(() => {
    if (!targetMacros || personal === null) return [];
    const score = (m) => Math.abs((m.calories || 0) - targetMacros.kcal) + Math.abs((m.protein || 0) - targetMacros.protein) * 3;
    const list = [
      ...(personal || []).map((r) => ({ r, source: 'personal', name: r.name, macros: r.macros })),
      ...(library || []).map((r) => ({ r, source: 'library', name: en ? r.name_en : r.name_fr, macros: r.macros })),
    ];
    return list.sort((a, b) => score(a.macros) - score(b.macros)).slice(0, 3);
  }, [personal, library, targetMacros, en]);

  useEffect(() => {
    if (tab === 'library' && library === null && premium) {
      api('/api/recipes').then((d) => setLibrary(d.recipes || [])).catch(() => setLibrary([]));
    }
  }, [tab, library, premium, api]);

  const submitManual = () => {
    if (!manual.food_name.trim() || !manual.calories) {
      toast.error(en ? 'Name and calories required' : 'Nom et calories requis');
      return;
    }
    onPicked({
      type: 'manual', food_name: manual.food_name.trim(), calories: parseFloat(manual.calories) || 0,
      protein: parseFloat(manual.protein) || 0, carbs: parseFloat(manual.carbs) || 0, fat: parseFloat(manual.fat) || 0,
    });
  };

  const RecipeRow = ({ r, source, name, macros }) => (
    <button
      onClick={() => onPicked({ type: 'recipe', recipe_id: r.id, recipe_source: source })}
      disabled={busy}
      className="w-full flex items-center gap-3 rounded-xl border border-white/10 bg-zinc-800/50 hover:border-primary/50 px-3 py-2.5 text-left transition-colors disabled:opacity-60"
      data-testid={`swap-recipe-${r.id}`}
    >
      {r.image_url ? <img src={r.image_url} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0" /> : <div className="w-11 h-11 rounded-lg bg-zinc-700 flex items-center justify-center shrink-0"><ChefHat className="w-4 h-4 text-zinc-400" /></div>}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-white truncate">{name}</p>
        <p className="text-xs text-zinc-500">{Math.round(macros.calories)} kcal · P {Math.round(macros.protein)}g</p>
      </div>
    </button>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[220] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="swap-modal">
      <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-md bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><Repeat className="w-5 h-5 text-primary" /> {en ? 'Replace this dish' : 'Remplacer ce plat'}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="swap-modal-close"><X className="w-5 h-5" /></button>
        </div>

        {suggestions.length > 0 && (
          <div className="mb-4" data-testid="swap-suggestions">
            <p className="text-xs font-bold text-yellow-300 mb-2 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> {en ? 'Similar macros — quick swap' : 'Macros similaires — remplacement rapide'}</p>
            <div className="grid grid-cols-3 gap-2">
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => onPicked({ type: 'recipe', recipe_id: s.r.id, recipe_source: s.source })} disabled={busy}
                  className="rounded-xl border border-yellow-500/30 bg-yellow-500/10 hover:bg-yellow-500/20 p-2 text-center transition-colors disabled:opacity-60"
                  data-testid={`swap-suggestion-${i}`}>
                  <p className="text-[11px] font-bold text-white leading-tight line-clamp-2">{s.name}</p>
                  <p className="text-[10px] text-yellow-300 mt-1">{Math.round(s.macros.calories)} kcal</p>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-1.5 mb-4">
          {[{ k: 'personal', l: en ? 'My recipes' : 'Mes recettes' }, { k: 'library', l: en ? 'Library' : 'Bibliothèque' }, { k: 'manual', l: en ? 'Custom food' : 'Aliment perso' }].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`py-2 rounded-lg text-xs font-bold border transition-colors ${tab === t.k ? 'bg-primary/20 border-primary text-white' : 'border-white/10 text-zinc-400 hover:border-primary/40'}`}
              data-testid={`swap-tab-${t.k}`}>{t.l}</button>
          ))}
        </div>

        {tab === 'personal' && (
          <div className="space-y-2" data-testid="swap-list-personal">
            {personal === null && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}
            {personal && personal.length === 0 && <p className="text-sm text-zinc-500 text-center py-6">{en ? 'No personal recipes yet — generate a meal plan first.' : 'Aucune recette personnelle — générez un plan alimentaire pour commencer.'}</p>}
            {personal && personal.map((r) => <RecipeRow key={r.id} r={r} source="personal" name={r.name} macros={r.macros} />)}
          </div>
        )}

        {tab === 'library' && (
          <div className="space-y-2" data-testid="swap-list-library">
            {premium === false && (
              <div className="text-center py-6">
                <Sparkles className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                <p className="text-sm text-zinc-400">{en ? 'The official recipe library requires a Suivi Pro subscription (9.90 CHF/month+).' : 'La bibliothèque officielle est réservée aux abonnés Suivi Pro (9.90 CHF/mois+).'}</p>
              </div>
            )}
            {premium && library === null && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}
            {premium && library && library.map((r) => <RecipeRow key={r.id} r={r} source="library" name={en ? r.name_en : r.name_fr} macros={r.macros} />)}
          </div>
        )}

        {tab === 'manual' && (
          <div className="space-y-3" data-testid="swap-manual-form">
            <input value={manual.food_name} onChange={(e) => setManual((m) => ({ ...m, food_name: e.target.value }))}
              placeholder={en ? 'Food name' : "Nom de l'aliment"}
              className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:border-primary focus:outline-none"
              data-testid="swap-manual-name" />
            <div className="grid grid-cols-4 gap-2">
              {[['calories', en ? 'Kcal' : 'Kcal'], ['protein', 'P (g)'], ['carbs', 'G (g)'], ['fat', 'L (g)']].map(([k, ph]) => (
                <input key={k} type="number" value={manual[k]} onChange={(e) => setManual((m) => ({ ...m, [k]: e.target.value }))}
                  placeholder={ph} className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2 py-2.5 text-sm text-white placeholder-zinc-600 focus:border-primary focus:outline-none"
                  data-testid={`swap-manual-${k}`} />
              ))}
            </div>
            <button onClick={submitManual} disabled={busy}
              className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.01] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="swap-confirm-manual">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {en ? 'Use this food' : 'Utiliser cet aliment'}
            </button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

/* ---------- Liste de courses (agrégée par l'IA, cochable, disponible hors-ligne) ---------- */
const ShoppingListModal = ({ shoppingList, en, calculationId, onClose }) => {
  const { authFetch } = useClientAuth();
  const storageKey = `shopping-list-checked-${calculationId}`;
  const cacheKey = `shopping-list-cache-${calculationId}`;
  const [checked, setChecked] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey)) || {}; } catch { return {}; }
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [isOffline, setIsOffline] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false);
  const [effectiveList, setEffectiveList] = useState(shoppingList);
  const [usingCache, setUsingCache] = useState(false);

  useEffect(() => {
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);

  useEffect(() => {
    const hasData = shoppingList && Object.values(shoppingList).some((items) => items && items.length > 0);
    if (hasData) {
      try { localStorage.setItem(cacheKey, JSON.stringify(shoppingList)); } catch { /* ignore */ }
      setEffectiveList(shoppingList);
      setUsingCache(false);
    } else {
      try {
        const cached = JSON.parse(localStorage.getItem(cacheKey));
        if (cached && Object.values(cached).some((items) => items && items.length > 0)) {
          setEffectiveList(cached);
          setUsingCache(true);
          return;
        }
      } catch { /* ignore */ }
      setEffectiveList(shoppingList);
      setUsingCache(false);
    }
  }, [shoppingList, cacheKey]);

  const toggle = (key) => {
    setChecked((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const categories = Object.entries(effectiveList || {}).filter(([, items]) => items && items.length > 0);

  const handlePrint = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    const body = categories.map(([cat, items]) => `
      <h3 style="color:#B92074;font-size:13px;text-transform:uppercase;letter-spacing:.03em;margin:18px 0 6px;">${cat}</h3>
      ${items.map((item, i) => `<p style="margin:3px 0;font-size:14px;${checked[`${cat}-${i}`] ? 'color:#999;text-decoration:line-through;' : 'color:#111;'}">${checked[`${cat}-${i}`] ? '☑' : '☐'}&nbsp; ${item}</p>`).join('')}
    `).join('');
    win.document.write(`<html><head><title>${en ? 'Shopping list' : 'Liste de courses'}</title></head><body style="font-family:Arial,sans-serif;max-width:480px;margin:24px auto;">
      <h2 style="color:#111;">${en ? 'Your weekly shopping list' : 'Votre liste de courses (semaine)'}</h2>${body}</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const handleEmail = async () => {
    if (isOffline) { toast.error(en ? 'You are offline — connect to send the email' : "Vous êtes hors-ligne — connectez-vous pour envoyer l'email"); return; }
    setSendingEmail(true);
    try {
      const res = await authFetch(`/api/tracking/meal-plan/${calculationId}/shopping-list/email`, {
        method: 'POST', body: JSON.stringify({ checked, lang: en ? 'en' : 'fr' }),
      });
      if (!res.ok) throw new Error();
      toast.success(en ? 'Shopping list sent to your inbox!' : 'Liste de courses envoyée dans votre boîte mail !');
    } catch { toast.error(en ? 'Could not send the email' : "Impossible d'envoyer l'email"); }
    finally { setSendingEmail(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[220] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="shopping-list-modal">
      <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-md bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-primary" /> {en ? 'Weekly shopping list' : 'Liste de courses (semaine)'}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="shopping-list-close"><X className="w-5 h-5" /></button>
        </div>
        {(isOffline || usingCache) && (
          <div className="mb-4 flex items-center gap-2 rounded-xl bg-yellow-500/10 border border-yellow-500/30 px-3 py-2" data-testid="shopping-list-offline-banner">
            <WifiOff className="w-4 h-4 text-yellow-300 shrink-0" />
            <p className="text-xs text-yellow-200">{en ? 'Offline mode — showing your last saved shopping list.' : 'Mode hors-ligne — affichage de votre dernière liste enregistrée.'}</p>
          </div>
        )}
        {categories.length > 0 && (
          <div className="flex gap-2 mb-4">
            <button onClick={handlePrint} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold border border-white/10 text-zinc-300 hover:border-white/30 transition-colors" data-testid="shopping-list-print-btn">
              <Printer className="w-3.5 h-3.5" /> {en ? 'Print' : 'Imprimer'}
            </button>
            <button onClick={handleEmail} disabled={sendingEmail || isOffline} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold border border-white/10 text-zinc-300 hover:border-primary/50 hover:text-primary transition-colors disabled:opacity-60" data-testid="shopping-list-email-btn">
              {sendingEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} {en ? 'Email it' : 'Envoyer par email'}
            </button>
          </div>
        )}
        {categories.length === 0 && <p className="text-sm text-zinc-500 text-center py-8">{en ? 'No shopping list for this plan.' : 'Aucune liste de courses pour ce plan.'}</p>}
        {categories.map(([cat, items]) => (
          <div key={cat} className="mb-4">
            <p className="text-xs font-bold text-primary uppercase tracking-wide mb-2">{cat}</p>
            <ul className="space-y-1.5">
              {items.map((item, i) => {
                const k = `${cat}-${i}`;
                return (
                  <li key={k}>
                    <button onClick={() => toggle(k)} className="w-full flex items-center gap-2.5 text-left" data-testid={`shopping-item-${k}`}>
                      <span className={`w-4.5 h-4.5 shrink-0 rounded-md border flex items-center justify-center transition-colors ${checked[k] ? 'bg-primary border-primary' : 'border-white/20'}`}>
                        {checked[k] && <Check className="w-3 h-3 text-white" />}
                      </span>
                      <span className={`text-sm ${checked[k] ? 'text-zinc-500 line-through' : 'text-zinc-200'}`}>{item}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </motion.div>
    </motion.div>
  );
};

/* ---------- Carte d'un repas ---------- */
const MealCard = ({ day, meal, dayIdx, mealIdx, en, onSwap, onReset, onLog, busyKey, selectionMode, isSelected, onToggleSelect }) => {
  const [date, setDate] = useState(todayStr());
  const key = `${dayIdx}-${mealIdx}`;
  const items = meal.items || [];
  const totals = items.reduce((acc, it) => ({
    kcal: acc.kcal + (it.kcal ?? (4 * (it.protein || 0) + 4 * (it.carbs || 0) + 9 * (it.fat || 0))),
    protein: acc.protein + (it.protein || 0), carbs: acc.carbs + (it.carbs || 0), fat: acc.fat + (it.fat || 0),
  }), { kcal: 0, protein: 0, carbs: 0, fat: 0 });

  return (
    <div
      onClick={selectionMode ? () => onToggleSelect(key) : undefined}
      className={`relative rounded-2xl bg-white/[0.035] backdrop-blur-xl border p-4 transition-colors ${selectionMode ? 'cursor-pointer' : ''} ${isSelected ? 'border-primary' : 'border-white/10'}`}
      data-testid={`calendar-meal-card-${dayIdx}-${mealIdx}`}
    >
      {selectionMode && (
        <div className="absolute top-3 right-3 z-10" data-testid={`calendar-meal-select-check-${key}`}>
          {isSelected ? <CheckSquare className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5 text-zinc-500" />}
        </div>
      )}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <p className="text-xs text-zinc-500">{meal.time}</p>
          <p className="font-bold text-white text-sm truncate">{meal.name}{meal.tag ? <span className="text-primary font-normal"> — {meal.tag}</span> : ''}</p>
        </div>
        {meal.replaced && !selectionMode && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-yellow-500/15 border border-yellow-500/40 text-yellow-300 shrink-0">
            <Repeat className="w-2.5 h-2.5" /> {en ? 'Swapped' : 'Remplacé'}
          </span>
        )}
      </div>
      <ul className="space-y-1 mb-3">
        {items.map((it, i) => (
          <li key={i} className="flex items-center justify-between text-sm text-zinc-300 gap-2">
            <span className="truncate min-w-0 flex-1">{it.grams ? `${it.grams} g ` : ''}{it.food}</span>
            <span className="text-xs text-zinc-500 shrink-0">{Math.round(it.kcal ?? 0)} kcal</span>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-3 text-xs text-zinc-400 mb-3 rounded-xl bg-zinc-800/50 border border-white/10 p-2.5 overflow-hidden">
        <span className="font-bold text-white text-sm inline-flex items-center gap-1 shrink-0"><Flame className="w-3.5 h-3.5 text-primary shrink-0" /> {Math.round(totals.kcal)} kcal</span>
        <span className="inline-flex items-center gap-1 shrink-0"><Beef className="w-3.5 h-3.5 text-pink-400 shrink-0" /> {Math.round(totals.protein)}g</span>
        <span className="inline-flex items-center gap-1 shrink-0"><Wheat className="w-3.5 h-3.5 text-yellow-400 shrink-0" /> {Math.round(totals.carbs)}g</span>
        <span className="inline-flex items-center gap-1 shrink-0"><Droplet className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> {Math.round(totals.fat)}g</span>
      </div>
      {!selectionMode && (
        <div className="flex items-center gap-1.5 sm:gap-2">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="w-[104px] shrink-0 bg-zinc-800 border border-white/10 rounded-lg px-1 py-2 text-[11px] text-white focus:border-primary focus:outline-none"
            data-testid={`calendar-log-date-${key}`} />
          <button onClick={() => onLog(dayIdx, mealIdx, date)} disabled={busyKey === `log-${key}`}
            className="flex-1 min-w-0 py-2 px-1 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.01] transition-transform disabled:opacity-60 flex items-center justify-center gap-1"
            data-testid={`calendar-log-btn-${key}`}>
            {busyKey === `log-${key}` ? <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" /> : <Plus className="w-3.5 h-3.5 shrink-0" />} <span className="truncate">{en ? 'Log' : 'Ajouter'}</span>
          </button>
          <button onClick={() => onSwap(dayIdx, mealIdx, totals)} className="shrink-0 p-2 rounded-lg border border-white/10 text-zinc-400 hover:text-primary hover:border-primary/40 transition-colors" data-testid={`calendar-swap-btn-${key}`} title={en ? 'Replace' : 'Remplacer'}>
            <Repeat className="w-4 h-4" />
          </button>
          {meal.replaced && (
            <button onClick={() => onReset(dayIdx, mealIdx)} disabled={busyKey === `reset-${key}`} className="shrink-0 p-2 rounded-lg border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 transition-colors" data-testid={`calendar-reset-btn-${key}`} title={en ? 'Reset' : 'Réinitialiser'}>
              {busyKey === `reset-${key}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/* ---------- Calendrier complet du plan (7 jours) ---------- */
export const MealPlanCalendar = ({ calculationId, initialDays, initialShoppingList, en }) => {
  const { authFetch, isAuthed } = useClientAuth();
  const api = useCallback((path, opts) => authFetch(path, opts).then((r) => (r.ok ? r.json() : Promise.reject(r))), [authFetch]);
  const [days, setDays] = useState(initialDays || []);
  const [activeDay, setActiveDay] = useState(0);
  const [swapTarget, setSwapTarget] = useState(null);
  const [showShoppingList, setShowShoppingList] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMeals, setSelectedMeals] = useState(new Set());
  const [smartListOpen, setSmartListOpen] = useState(false);
  const dayNames = en ? DAYS.en : DAYS.fr;
  const dayTabRefs = React.useRef([]);

  const toggleMealSelect = (key) => setSelectedMeals((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });
  const selectAllInDay = () => setSelectedMeals((prev) => {
    const next = new Set(prev);
    (day.meals || []).forEach((_, mi) => next.add(`${activeDay}-${mi}`));
    return next;
  });
  const selectedIngredients = Array.from(selectedMeals).flatMap((k) => {
    const [di, mi] = k.split('-').map(Number);
    const items = days[di]?.meals?.[mi]?.items || [];
    return items.map((it) => (it.grams ? `${it.grams} g de ${it.food}` : it.food));
  });

  useEffect(() => {
    dayTabRefs.current[activeDay]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [activeDay]);

  if (!isAuthed) {
    return (
      <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-8 text-center" data-testid="meal-plan-calendar-locked">
        <Lock className="w-8 h-8 text-zinc-500 mx-auto mb-3" />
        <p className="text-zinc-400 text-sm">{en ? 'Log in to your Body-Up account to add these meals to your tracking.' : 'Connectez-vous à votre espace Body-Up pour ajouter ces repas à votre suivi.'}</p>
        <a href="/espace-client" className="inline-block mt-4 text-primary font-semibold hover:underline">{en ? 'Log in' : 'Se connecter'}</a>
      </div>
    );
  }

  const day = days[activeDay];

  const doLog = async (dayIdx, mealIdx, date) => {
    setBusyKey(`log-${dayIdx}-${mealIdx}`);
    try {
      await api(`/api/tracking/meal-plan/${calculationId}/day/${dayIdx}/meal/${mealIdx}/log`, { method: 'POST', body: JSON.stringify({ date }) });
      toast.success(en ? 'Added to your tracking!' : 'Ajouté à votre suivi !');
    } catch { toast.error(en ? 'Could not log this meal' : "Impossible d'ajouter ce repas"); }
    finally { setBusyKey(null); }
  };

  const doReset = async (dayIdx, mealIdx) => {
    setBusyKey(`reset-${dayIdx}-${mealIdx}`);
    try {
      const d = await api(`/api/tracking/meal-plan/${calculationId}/day/${dayIdx}/meal/${mealIdx}/reset`, { method: 'POST' });
      setDays((prev) => { const next = [...prev]; next[dayIdx] = { ...next[dayIdx], meals: next[dayIdx].meals.map((m, i) => (i === mealIdx ? d.meal : m)) }; return next; });
      toast.success(en ? 'Original meal restored' : 'Repas d\'origine restauré');
    } catch { toast.error(en ? 'Could not reset' : 'Impossible de réinitialiser'); }
    finally { setBusyKey(null); }
  };

  const doSwap = async (payload) => {
    setBusyKey('swap');
    try {
      const { dayIdx, mealIdx } = swapTarget;
      const d = await api(`/api/tracking/meal-plan/${calculationId}/day/${dayIdx}/meal/${mealIdx}/replace`, { method: 'POST', body: JSON.stringify(payload) });
      setDays((prev) => { const next = [...prev]; next[dayIdx] = { ...next[dayIdx], meals: next[dayIdx].meals.map((m, i) => (i === mealIdx ? d.meal : m)) }; return next; });
      toast.success(en ? 'Dish replaced!' : 'Plat remplacé !');
      setSwapTarget(null);
    } catch { toast.error(en ? 'Could not replace this dish' : 'Impossible de remplacer ce plat'); }
    finally { setBusyKey(null); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await authFetch(`/api/tracking/meal-plan/${calculationId}/calendar/pdf?lang=${en ? 'en' : 'fr'}`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'plan-alimentaire-body-up.pdf'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(en ? 'Could not export the PDF' : "Impossible d'exporter le PDF"); }
    finally { setDownloading(false); }
  };

  return (
    <div data-testid="meal-plan-calendar" className="overflow-x-hidden">
      <div className="mb-5">
        <div className="relative">
          <div className="flex gap-2 overflow-x-auto pb-1.5 -mx-1 px-1 snap-x snap-mandatory scroll-smooth" data-testid="calendar-day-tabs">
            {days.map((d, i) => (
              <button key={i} ref={(el) => (dayTabRefs.current[i] = el)} onClick={() => setActiveDay(i)}
                className={`shrink-0 snap-start whitespace-nowrap px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-bold border transition-colors inline-flex items-center gap-1.5 ${activeDay === i ? 'bg-primary border-primary text-white' : 'border-white/10 text-zinc-400 hover:border-primary/40 hover:text-white'}`}
                data-testid={`calendar-day-tab-${i}`}>
                {d.training_day ? <Dumbbell className="w-3.5 h-3.5 shrink-0" /> : <Moon className="w-3.5 h-3.5 shrink-0" />}
                <span className="sm:hidden">{(dayNames[i] || d.day).slice(0, 3)}</span>
                <span className="hidden sm:inline">{dayNames[i] || d.day}</span>
              </button>
            ))}
          </div>
          <div className="pointer-events-none absolute right-0 top-0 bottom-1.5 w-7 bg-gradient-to-l from-black/70 to-transparent sm:hidden" />
        </div>
        <div className="flex items-center gap-2 mt-2.5">
          <button onClick={downloadPdf} disabled={downloading}
            className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold border border-white/10 text-zinc-300 hover:border-yellow-400/50 hover:text-yellow-300 transition-colors disabled:opacity-60"
            data-testid="calendar-download-pdf-btn">
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PDF
          </button>
          {initialShoppingList && Object.keys(initialShoppingList).length > 0 && (
            <button onClick={() => setShowShoppingList(true)}
              className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold border border-white/10 text-zinc-300 hover:border-primary/50 hover:text-primary transition-colors"
              data-testid="calendar-shopping-list-btn">
              <ShoppingCart className="w-3.5 h-3.5" /> {en ? 'Groceries' : 'Courses'}
            </button>
          )}
          <button
            onClick={() => { setSelectionMode((v) => !v); setSelectedMeals(new Set()); }}
            className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold border transition-colors ${selectionMode ? 'bg-primary border-primary text-white' : 'border-white/10 text-zinc-300 hover:border-primary/50 hover:text-primary'}`}
            data-testid="calendar-selection-toggle-btn">
            <ListChecks className="w-3.5 h-3.5" /> {selectionMode ? (en ? 'Cancel' : 'Annuler') : (en ? 'Pick meals' : 'Choisir des repas')}
          </button>
        </div>
        {selectionMode && (
          <div className="sticky top-2 z-10 mt-3 flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-primary/15 border border-primary/40 px-4 py-2.5" data-testid="meal-selection-action-bar">
            <p className="text-sm text-white font-semibold">{en ? `${selectedMeals.size} meal(s) selected` : `${selectedMeals.size} repas sélectionné(s)`}</p>
            <div className="flex items-center gap-2">
              <button onClick={selectAllInDay} className="px-3 py-1.5 rounded-full text-xs font-bold text-primary border border-primary/40" data-testid="calendar-select-all-day-btn">
                {en ? 'Select this day' : 'Tout ce jour'}
              </button>
              <button
                onClick={() => setSmartListOpen(true)}
                disabled={selectedMeals.size === 0}
                className="px-4 py-1.5 rounded-full text-xs font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-40"
                data-testid="calendar-generate-smart-list-btn">
                {en ? 'Generate list' : 'Générer la liste'}
              </button>
            </div>
          </div>
        )}
      </div>

      {day && (
        <div className="grid sm:grid-cols-2 gap-4" data-testid={`calendar-day-view-${activeDay}`}>
          {(day.meals || []).map((meal, mi) => (
            <MealCard key={mi} day={day} meal={meal} dayIdx={activeDay} mealIdx={mi} en={en}
              onSwap={(di, mIdx, totals) => setSwapTarget({ dayIdx: di, mealIdx: mIdx, totals })} onReset={doReset} onLog={doLog} busyKey={busyKey}
              selectionMode={selectionMode} isSelected={selectedMeals.has(`${activeDay}-${mi}`)} onToggleSelect={toggleMealSelect} />
          ))}
        </div>
      )}

      <AnimatePresence>
        {swapTarget && <SwapModal en={en} api={api} targetMacros={swapTarget.totals} onClose={() => setSwapTarget(null)} onPicked={doSwap} busy={busyKey === 'swap'} />}
        {showShoppingList && <ShoppingListModal shoppingList={initialShoppingList} en={en} calculationId={calculationId} onClose={() => setShowShoppingList(false)} />}
        {smartListOpen && (
          <SmartShoppingListModal
            rawIngredients={selectedIngredients}
            sourceLabel={en ? `${selectedMeals.size} selected meal(s)` : `${selectedMeals.size} repas sélectionné(s)`}
            en={en} onClose={() => setSmartListOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default MealPlanCalendar;
